import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Download, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import site from "@/config/site";
import { submitAudit } from "@/lib/api";

const QUESTIONS = [
  { q: "Can a stranger understand what you sell within 5 seconds of landing on your profile or website?", dim: "Message Clarity" },
  { q: "Do you have one clearly defined ideal customer — not 'everyone who can pay'?", dim: "Positioning" },
  { q: "Is your brand visually consistent across Instagram, website, packaging and ads?", dim: "Visual Identity" },
  { q: "Does your website or landing page convert visitors into real enquiries every week?", dim: "Website Conversion" },
  { q: "Do you post content consistently — at least 3–4 times a week, every week?", dim: "Content Engine" },
  { q: "Do you know exactly where your last 10 customers came from?", dim: "Tracking" },
  { q: "Do you have a follow-up system for people who enquire but don't buy?", dim: "Sales Follow-up" },
  { q: "Are you running ads with a defined funnel — not just boosting posts?", dim: "Paid Funnel" },
  { q: "Do you know your customer acquisition cost and average order value?", dim: "Numbers" },
  { q: "Do you have a written 90-day growth plan for your brand?", dim: "Strategy" },
];

const OPTIONS = [
  { label: "Yes, solid", points: 2 },
  { label: "Somewhat", points: 1 },
  { label: "No / not sure", points: 0 },
];

const inputCls =
  "w-full bg-[#0A0F08] border border-[#27331C] rounded-xl px-4 py-3.5 text-sm text-[#F2F1E8] placeholder:text-[#5A5D4E] focus:outline-none focus:border-[var(--accent)] transition-colors duration-300";

const ScoreRing = ({ score, max }) => {
  const pct = score / max;
  const R = 84;
  const C = 2 * Math.PI * R;
  return (
    <div className="relative w-52 h-52 mx-auto">
      <svg viewBox="0 0 200 200" className="w-full h-full -rotate-90">
        <circle cx="100" cy="100" r={R} fill="none" stroke="#27331C" strokeWidth="10" />
        <motion.circle
          cx="100" cy="100" r={R} fill="none" stroke="var(--accent)" strokeWidth="10" strokeLinecap="round"
          strokeDasharray={C}
          initial={{ strokeDashoffset: C }}
          animate={{ strokeDashoffset: C * (1 - pct) }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span data-testid="audit-score" className="font-display text-6xl text-[#F2F1E8]">{score}<span className="text-2xl text-[#8A8C7E]">/{max}</span></span>
        <span className="font-mono2 text-[10px] tracking-[0.25em] text-[#8A8C7E] mt-1">BRAND HEALTH</span>
      </div>
    </div>
  );
};

export default function Audit() {
  const [step, setStep] = useState(-1); // -1 = details, 0..9 = questions, 10 = submitting
  const [details, setDetails] = useState({ name: "", email: "", phone: "", brand: "", website: "" });
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const totalSteps = QUESTIONS.length + 1;
  const progress = result ? 100 : ((step + 1) / totalSteps) * 100;

  const setD = (k) => (e) => setDetails({ ...details, [k]: e.target.value });

  const startQuiz = (e) => {
    e.preventDefault();
    if (!details.name || !details.email || !details.phone || !details.brand) {
      toast.error("Please fill name, email, phone and brand name.");
      return;
    }
    setStep(0);
  };

  const answer = async (opt) => {
    const next = [...answers, { question: QUESTIONS[step].q, answer: opt.label, points: opt.points, dim: QUESTIONS[step].dim }];
    setAnswers(next);
    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      setSubmitting(true);
      try {
        const res = await submitAudit({
          ...details,
          answers: next.map(({ question, answer, points }) => ({ question, answer, points })),
        });
        const weakDims = next.filter((a) => a.points <= 1).slice(0, 3).map((a) => a.dim);
        setResult({ ...res, weakDims });
      } catch {
        toast.error("Could not save your audit. Please try again.");
      } finally {
        setSubmitting(false);
      }
    }
  };

  const downloadSummary = () => {
    if (!result) return;
    const lines = [
      "SANVALIO — FREE BRAND AUDIT",
      "================================",
      `Brand: ${details.brand}`,
      `Name: ${details.name}`,
      `Score: ${result.score}/${result.max_score}`,
      `Verdict: ${result.verdict}`,
      "",
      result.line,
      "",
      "TOP LEAKS:",
      ...result.weakDims.map((d, i) => `${i + 1}. ${d}`),
      "",
      "NEXT STEPS:",
      "Book the ₹999 Growth Call — get your full 90-day growth roadmap,",
      "and the ₹999 is fully adjusted when you execute with us.",
      "",
      "sanvalio.com · design4you.co.in",
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `brand-audit-${details.brand.replace(/\s+/g, "-").toLowerCase()}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div data-testid="audit-page" className="min-h-screen bg-[#0A0F08] text-[#F2F1E8] flex flex-col">
      {/* top bar */}
      <div className="border-b border-[#27331C]">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" data-testid="audit-home-link">
            <img src="/logo-sanvalio.png" alt="Sanvalio" className="h-8 w-auto" />
          </Link>
          <span className="font-mono2 text-[10px] tracking-[0.3em] text-[#8A8C7E]">FREE BRAND AUDIT</span>
        </div>
        <div className="h-0.5 bg-[#27331C]">
          <motion.div className="h-full bg-[var(--accent)]" animate={{ width: `${progress}%` }} transition={{ duration: 0.5 }} />
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-5 py-16">
        <div className="w-full max-w-2xl">
          <AnimatePresence mode="wait">
            {result ? (
              <motion.div key="result" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} data-testid="audit-result">
                <ScoreRing score={result.score} max={result.max_score} />
                <h2 className="mt-8 text-center font-display text-4xl sm:text-5xl text-[#F2F1E8]">{result.verdict}</h2>
                <p className="mt-4 text-center text-sm sm:text-base text-[#B9BBAC] leading-relaxed max-w-lg mx-auto">{result.line}</p>

                {result.weakDims.length > 0 && (
                  <div className="mt-8 rounded-2xl border border-[#E4B54A]/40 bg-[#E4B54A]/5 p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-4 h-4 text-[#E4B54A]" />
                      <span className="font-mono2 text-[10px] tracking-[0.25em] text-[#E4B54A]">YOUR BIGGEST LEAKS</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {result.weakDims.map((d) => (
                        <span key={d} data-testid={`weak-area-${d.replace(/\s+/g, "-").toLowerCase()}`}
                          className="text-xs text-[#F2F1E8] border border-[#27331C] rounded-full px-4 py-2">{d}</span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-9">
                  <button
                    data-testid="audit-pay-999-btn"
                    onClick={() => navigate("/book?plan=growth")}
                    className="group w-full flex items-center justify-center gap-2 bg-[var(--accent)] text-[#0A0F08] font-bold text-sm px-6 py-4 rounded-full hover:bg-[#F2F1E8] transition-colors duration-300"
                  >
                    Book ₹999 Growth Call — full roadmap included
                    <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                </div>
                <button
                  data-testid="audit-download-btn"
                  onClick={downloadSummary}
                  className="mt-4 w-full flex items-center justify-center gap-2 text-sm text-[#8A8C7E] hover:text-[var(--accent)] transition-colors duration-300 py-3"
                >
                  <Download className="w-4 h-4" /> Download my audit summary
                </button>
                <p className="mt-2 text-center font-mono2 text-[10px] tracking-[0.15em] text-[#5A5D4E]">
                  A COPY OF YOUR RESULT HAS BEEN SENT TO YOUR EMAIL
                </p>
              </motion.div>
            ) : submitting ? (
              <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center" data-testid="audit-submitting">
                <Loader2 className="w-10 h-10 text-[var(--accent)] animate-spin mx-auto mb-5" />
                <p className="font-display text-2xl">Scoring your brand…</p>
              </motion.div>
            ) : step === -1 ? (
              <motion.div key="details" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.6 }}>
                <span className="font-mono2 text-[10px] tracking-[0.3em] text-[var(--accent)]">BEFORE WE START</span>
                <h1 className="mt-3 font-display text-4xl sm:text-5xl leading-[1.05]">
                  10 brutally honest questions.<br />
                  <span className="italic font-light text-[var(--accent)]">One instant score.</span>
                </h1>
                <p className="mt-4 text-sm text-[#8A8C7E]">Your result and detailed report options land on your email. No spam, ever.</p>
                <form onSubmit={startQuiz} className="mt-8 space-y-4" data-testid="audit-details-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input data-testid="audit-name-input" className={inputCls} placeholder="Your name *" value={details.name} onChange={setD("name")} />
                    <input data-testid="audit-phone-input" className={inputCls} placeholder="Phone / WhatsApp *" value={details.phone} onChange={setD("phone")} />
                  </div>
                  <input data-testid="audit-email-input" type="email" className={inputCls} placeholder="Email address *" value={details.email} onChange={setD("email")} />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input data-testid="audit-brand-input" className={inputCls} placeholder="Brand / business name *" value={details.brand} onChange={setD("brand")} />
                    <input data-testid="audit-website-input" className={inputCls} placeholder="Website or Instagram (optional)" value={details.website} onChange={setD("website")} />
                  </div>
                  <button type="submit" data-testid="audit-start-btn"
                    className="group w-full flex items-center justify-center gap-3 bg-[var(--accent)] text-[#0A0F08] font-bold text-sm sm:text-base px-8 py-4 rounded-full hover:bg-[#F2F1E8] transition-colors duration-300">
                    Start My Free Audit
                    <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </form>
              </motion.div>
            ) : (
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="font-mono2 text-[10px] tracking-[0.3em] text-[#8A8C7E]">
                    QUESTION {step + 1} / {QUESTIONS.length}
                  </span>
                  <span className="font-mono2 text-[10px] tracking-[0.3em] text-[var(--accent)]">{QUESTIONS[step].dim.toUpperCase()}</span>
                </div>
                <h2 data-testid={`audit-question-${step}`} className="font-display text-2xl sm:text-4xl leading-[1.15] text-[#F2F1E8]">
                  {QUESTIONS[step].q}
                </h2>
                <div className="mt-9 space-y-3">
                  {OPTIONS.map((opt) => (
                    <button
                      key={opt.label}
                      data-testid={`audit-option-${step}-${opt.points}`}
                      onClick={() => answer(opt)}
                      className="w-full text-left flex items-center justify-between border border-[#27331C] rounded-2xl px-6 py-5 text-sm sm:text-base text-[#B9BBAC] hover:border-[var(--accent)] hover:text-[#F2F1E8] hover:bg-[var(--accent)]/5 transition-colors duration-300"
                    >
                      {opt.label}
                      <ArrowRight className="w-4 h-4 opacity-40" />
                    </button>
                  ))}
                </div>
                {step > 0 && (
                  <button data-testid="audit-back-btn" onClick={() => { setStep(step - 1); setAnswers(answers.slice(0, -1)); }}
                    className="mt-6 flex items-center gap-2 text-xs text-[#5A5D4E] hover:text-[var(--accent)] transition-colors duration-300">
                    <ArrowLeft className="w-3.5 h-3.5" /> Previous question
                  </button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
